/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * BaLogin — single component for both auth stages (challenge → code). On a
 * successful sign-in it emits `ba-login` with `{ admin }`; the caller routes
 * to the shell (admin) or `<ba-not-authorized>` (non-admin). Lit light-DOM.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { authService, type ChallengeResponse } from '../services/auth.js';

export const BA_LOGIN_EVENT = 'ba-login';

export interface BaLoginDetail {
  admin: boolean;
}

@customElement('ba-login')
export class BaLogin extends LitElement {
  @state() private stage: 'challenge' | 'code' = 'challenge';
  @state() private email = '';

  private challengeResult: (ChallengeResponse & { ok: true }) | null = null;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override render(): TemplateResult {
    return html`
      <div class="ba-login-shell">
        <wa-card class="ba-login-card" appearance="accent">
          <div slot="header"><strong>BeyondDemise Admin</strong></div>
          <div id="error-message" role="alert" hidden></div>
          ${
            this.stage === 'challenge'
              ? this.renderChallenge()
              : this.renderCode()
          }
        </wa-card>
      </div>
    `;
  }

  private renderChallenge(): TemplateResult {
    return html`
      <form id="challenge-form" @submit=${this.onChallengeSubmit}>
        <wa-input
          type="email"
          name="email"
          required
          label="Admin email"
          placeholder="you@beyonddemise.com"
        ></wa-input>
        <wa-button type="submit" variant="brand">Request code</wa-button>
      </form>
    `;
  }

  private renderCode(): TemplateResult {
    return html`
      <form id="login-form" @submit=${this.onCodeSubmit}>
        <wa-input
          type="email"
          name="email"
          label="Admin email"
          .value=${this.email}
          disabled
        ></wa-input>
        <wa-input
          type="text"
          name="password"
          required
          label="Access code"
          autocomplete="one-time-code"
          inputmode="numeric"
        ></wa-input>
        <wa-button type="submit" variant="brand">Sign in</wa-button>
      </form>
    `;
  }

  private getErrorMessage(): HTMLElement {
    return this.querySelector<HTMLElement>('#error-message')!;
  }

  private async onChallengeSubmit(event: Event): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const result = await authService.formChallenge(
      form,
      this.getErrorMessage()
    );
    if (result.ok) {
      this.challengeResult = result;
      this.email = result.identifier;
      this.stage = 'code';
    }
  }

  private async onCodeSubmit(event: Event): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const codeInput = form.querySelector<HTMLInputElement>(
      'wa-input[name="password"]'
    );
    const submitBtn = form.querySelector<HTMLButtonElement>(
      'wa-button[type="submit"]'
    );
    if (!this.challengeResult || !codeInput || !submitBtn) {
      return;
    }
    const ok = await authService.challengeLogin(
      codeInput.value ?? '',
      this.challengeResult,
      this.getErrorMessage(),
      submitBtn
    );
    if (ok) {
      this.dispatchEvent(
        new CustomEvent<BaLoginDetail>(BA_LOGIN_EVENT, {
          detail: { admin: authService.isAdmin() },
          bubbles: true,
          composed: true,
        })
      );
    }
  }
}
